<template>
  <div ref="dropdownRef" class="relative inline-block">
    <button
      ref="triggerRef"
      type="button"
      :title="t('controls.language.select')"
      :aria-label="t('controls.language.select')"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      class="inline-flex items-center gap-2 min-h-[44px] py-2 px-3 -mx-3 rounded-sm cursor-pointer transition-colors"
      :class="triggerClass"
      @click="toggleDropdown"
      @keydown.escape.prevent="closeDropdown"
    >
      <NuxtImg
        :src="`/images/flags/${currentLocale}.svg`"
        :alt="currentLocaleName"
        width="20"
        height="20"
        loading="lazy"
        class="w-5 h-5 shrink-0 align-middle"
      />
      <span
        class="font-body text-xs uppercase tracking-[0.18em] leading-none"
      >
        {{ currentLocale }}
      </span>
      <Icon
        v-if="!compact"
        name="mdi-chevron-down"
        size="14px"
        class="transition-transform duration-200"
        :class="[chevronClass, isOpen ? 'rotate-180' : '']"
      />
    </button>

    <Transition
      enter-active-class="transition ease-out duration-150"
      enter-from-class="opacity-0 -translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div
        v-if="isOpen"
        role="listbox"
        :aria-label="t('controls.language.select')"
        class="absolute right-0 z-30 mt-3 w-44 origin-top-right rounded-sm bg-slate-900 border border-slate-700/60 shadow-lg focus:outline-none"
      >
        <ul class="py-1">
          <li
            v-for="localeItem in locales"
            :key="localeItem.code"
          >
            <a
              :href="switchLocalePath(localeItem.code)"
              role="option"
              :aria-selected="localeItem.code === currentLocale"
              class="flex items-center gap-3 w-full px-4 min-h-[44px] py-2.5 text-sm font-body text-slate-200 hover:bg-slate-800 hover:text-amber-400 cursor-pointer transition-colors no-underline"
              :class="localeItem.code === currentLocale ? 'border-l-2 border-red-500 pl-[14px]' : ''"
              @click.prevent="changeLocale(localeItem.code)"
              @keydown.escape.prevent="closeDropdown"
            >
              <NuxtImg
                :src="`/images/flags/${localeItem.code}.svg`"
                :alt="localeItem.name"
                width="16"
                height="16"
                loading="lazy"
                class="w-4 h-4 shrink-0"
              />
              <span class="flex-1 text-left">{{ localeItem.name }}</span>
              <span
                class="text-[10px] uppercase tracking-[0.2em] text-slate-500"
              >
                {{ localeItem.code }}
              </span>
            </a>
          </li>
        </ul>
      </div>
    </Transition>
  </div>
</template>

<script setup>
const props = defineProps({
  variant: {
    type: String,
    default: "dark",
    validator: (v) => ["dark", "light"].includes(v),
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

const { t, locales, locale, setLocale } = useI18n();
// Real <a href> per locale so crawlers can discover /, /ru/, /ka/ via the
// switcher (Lighthouse `crawlable-anchors` rule). Click handler stays — it
// preventDefaults the navigation and routes via setLocale to keep i18n
// state in sync without a full page reload.
const switchLocalePath = useSwitchLocalePath();
const { trackEvent } = useAnalytics();

const isOpen = ref(false);
const currentLocale = computed(() => locale.value);
const currentLocaleName = computed(
  () => locales.value.find((l) => l.code === currentLocale.value)?.name || currentLocale.value
);
const dropdownRef = ref(null);
const triggerRef = ref(null);

const triggerClass = computed(() =>
  props.variant === "dark"
    ? "text-slate-300 hover:text-amber-400 hover:bg-slate-800/60 focus-visible:outline-1 focus-visible:outline focus-visible:outline-amber-400/60 focus-visible:outline-offset-2"
    : "text-slate-700 dark:text-slate-200 hover:text-amber-500 hover:bg-slate-100 dark:hover:bg-slate-800 focus-visible:outline-1 focus-visible:outline focus-visible:outline-amber-500/60 focus-visible:outline-offset-2"
);

const chevronClass = computed(() =>
  props.variant === "dark" ? "text-slate-500" : "text-slate-400 dark:text-slate-500"
);

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const closeDropdown = () => {
  isOpen.value = false;
  nextTick(() => triggerRef.value?.focus());
};

const changeLocale = async (code) => {
  const from = locale.value;
  if (code !== from) {
    trackEvent('locale_change', { from, to: code });
  }
  await setLocale(code);
  isOpen.value = false;
};

const handleClickOutside = (event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    isOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>
